const VehicleType = {
    PASSENGER: 'Passenger',
    MOTORCYCLE: 'Motorcycle',
    TRUCK: 'Truck',
    COMMERCIAL: 'Commercial',
    TRAILER: 'Trailer',
    MOPED: 'Moped',
    VESSEL: 'Vessel',
    LOW_SPEED: 'LowSpeed',
    MOTORSCOOTER: 'Motorscooter',
    DUAL_SPORT: 'DualSport', // can be both on-road and off-road, but we treat it as street legal
    // off-road
    ATV: 'ATV',
    ORV: 'ORV',
    NON_TITLED_VEHICLE: 'NonTitledVehicle',
    OHV: 'OHV',
};
const VehicleTypeDescriptions = {
    [VehicleType.PASSENGER]: 'Standard passenger cars, SUVs, minivans, and sedans designed for on-road use; identified by 4 wheels, enclosed cabin, and passenger seating configuration.',
    [VehicleType.MOTORCYCLE]: 'Two-wheeled motorized vehicles with engines over 50cc capable of highway speeds; identified by handlebar steering, no pedals, and requiring motorcycle endorsement.',
    [VehicleType.TRUCK]: 'Pickup trucks and light-duty trucks designed for hauling cargo; identified by open cargo bed and typically higher ground clearance than passenger vehicles.',
    [VehicleType.COMMERCIAL]: 'Vehicles used primarily for business purposes including delivery vans, box trucks, and commercial fleet vehicles; identified by commercial use designation and often higher weight ratings.',
    [VehicleType.TRAILER]: 'Towed vehicles without self-propulsion used to transport cargo, boats, or other equipment; identified by hitch connection point and lack of engine.',
    [VehicleType.MOPED]: 'Small motorized bikes with engines 50cc or less and top speeds around 30 mph; identified by small engine size, often includes pedals, and automatic transmission.',
    [VehicleType.VESSEL]: 'Watercraft including boats, personal watercraft (jet skis), and marine vehicles; identified by hull design and marine propulsion systems.',
    [VehicleType.LOW_SPEED]: 'Street-legal Low-Speed Vehicles (LSVs) with max speed of 20-25 mph; MUST be street-legal with required DOT safety equipment: headlights, taillights, brake lights, turn signals, mirrors, horn, reflectors, seat belts, and DOT-approved windshield. Includes any vehicle (including golf carts) that is sold as street-legal or marketed as road-ready with full LSV safety compliance.',
    [VehicleType.MOTORSCOOTER]: 'Small motorized two-wheeled vehicles with step-through frame design; identified by platform floorboard, smaller wheels than motorcycles, and typically smaller engines.',
    [VehicleType.ATV]: 'All-Terrain Vehicles (four-wheelers/quads) designed exclusively for off-road use; identified by 4 wheels, handlebar steering, straddle seating, and knobby tires (not street legal).',
    [VehicleType.ORV]: 'Off-Road Vehicles including side-by-sides and UTVs designed for off-highway recreational use; identified by side-by-side seating, roll cage, and off-road suspension (not street legal).',
    [VehicleType.NON_TITLED_VEHICLE]: 'Small or low-speed vehicles that are NOT street-legal and are not titled or registered; intended for private property, gated communities, tracks, or designated paths only. Includes standard e-bikes, go-karts, bicycles, golf carts, and neighborhood vehicles under 20 mph without headlights, taillights, turn signals, mirrors, horn, seat belts, or DOT-approved windshield. Also includes mini bikes, pit bikes, and youth dirt bikes: small-displacement off-road motorcycles sized for children or pit use, such as the Massimo MB 200S, Honda CRF50F, and Yamaha TT-R50.',
    [VehicleType.DUAL_SPORT]: 'Motorcycles designed for both on-road and off-road use with street-legal equipment; identified by dual-purpose tires, higher ground clearance than standard motorcycles, and both DOT-approved lights and off-road capability.',
    [VehicleType.OHV]: 'Off-Highway Vehicles designed for off-road use only, NOT street-legal, with speeds exceeding 20 mph. Includes full-size dirt bikes and trail bikes, and high-speed off-road recreational vehicles (including modified or performance golf carts over 20 mph). Identified by off-road tires, minimal/no street-legal equipment, and recreational or utility off-highway purpose. Mini bikes, pit bikes, and youth dirt bikes are NonTitledVehicle, not OHV.',
};
/**
 * One line per body type, read by the AI adapters that classify a vehicle. Body
 * type is the vehicle's physical form, independent of how it titles (vehicleType).
 */
const BodyTypeDescriptions = {
    Ambulance: 'Emergency medical vehicle built on a van or truck chassis.',
    Bus: 'Large passenger vehicle seating many riders, such as a school, transit, or shuttle bus.',
    Cabriolet: 'Car with a folding soft or hard roof; the European name for a convertible.',
    CamperTrailer: 'Towed camper or travel trailer with living quarters.',
    Convertible: 'Car with a roof that folds or retracts.',
    Coupe2D: 'Two-door car with a fixed roof.',
    Hatchback3D: 'Three-door car with a rear hatch that opens upward.',
    Limousine: 'Stretched luxury passenger car.',
    ManufacturedHome: 'Factory-built home moved on a chassis.',
    Motorcycle: 'Street motorcycle: two wheels, saddle seat, built for paved roads.',
    MotorHome: 'Self-propelled RV with built-in living quarters.',
    MultipurposeTrailer: 'General-purpose towed trailer not built for one specific load.',
    PassengerVan: 'Van built to carry passengers rather than cargo.',
    Pickup: 'Light truck with an open cargo bed.',
    Roadster: 'Open two-seat sports vehicle, including three-wheel roadster motorcycles.',
    Sedan4D: 'Four-door car with a separate trunk.',
    SpecialMobileMachinery: 'Self-propelled construction or industrial equipment.',
    StationWagon5D: 'Car with an extended roof over a rear cargo area and a fifth door.',
    Tractor: 'Farm or yard tractor.',
    Trailer: 'Towed trailer with no more specific body type.',
    TrailerCoach: 'Towed coach or fifth-wheel trailer with living quarters.',
    TrailerUtility: 'Small towed utility trailer for cargo or equipment.',
    Truck: 'Truck with no more specific body type.',
    TruckTractor: 'Semi-truck cab that tows a trailer.',
    TruckVan: 'Cargo van or box van built to carry goods.',
    UtilityPassengerSUV: 'Sport utility vehicle built for passengers.',
    UtilityTruck: 'Older name for UTV. Choose UTV instead.',
    TwoDoor: 'Two-door enclosed passenger body.',
    FourDoor: 'Four-door enclosed passenger body.',
    Gokart: 'Small open four-wheel kart with a low seat, for tracks or private property.',
    'E-Bike': 'Bicycle with pedals and an electric assist motor.',
    Bicycle: 'Human-powered pedal bicycle with no motor.',
    GolfCart: 'Small open cart seating two to six, built for golf courses or neighborhoods.',
    ATV: 'Four-wheeled quad with handlebar steering and a straddle seat.',
    SideBySide: 'Off-road vehicle with side-by-side seats, steering wheel, and roll cage, built for sport or recreation.',
    UTV: 'Utility side-by-side with a cargo bed, built for work.',
    Scooter: 'Two-wheeler with a step-through frame and a floorboard.',
    Moped: 'Small low-powered two-wheeler, often with pedals.',
    Trike: 'Three-wheeled motorcycle.',
    Snowmobile: 'Tracked vehicle with front skis, built for snow.',
    DualSport: 'Motorcycle with lights and street-legal equipment that also rides off-road.',
    OffRoadMotorcycle: 'Off-road motorcycle with no street-legal equipment: dirt, trail, motocross, pit, mini, and youth bikes.',
    PontoonBoat: 'Flat-deck boat floating on pontoons.',
    FishingBoat: 'Boat built for fishing, such as a bass, jon, or aluminum fishing boat.',
    Bowrider: 'Open boat with seating in the bow.',
    CenterConsole: 'Open boat steered from a console in the middle of the deck.',
    DeckBoat: 'Open boat with a wide, flat deck carried forward to the bow.',
    CabinCruiser: 'Boat with an enclosed cabin.',
    Sailboat: 'Boat propelled mainly by sail.',
    Jetski: 'Stand-up or sit-down jet-propelled personal watercraft.',
    PersonalWatercraft: 'Sit-down jet-propelled watercraft, such as a Sea-Doo or WaveRunner.',
    BoatOther: 'Boat with no more specific body type.',
};
const VehicleCategory = {
    BICYCLE: 'Bicycle',
    SXS: 'Side-by-Side',
    ATV: 'ATV',
    DUAL_SPORT: 'Dual Sport',
    MOTORCYCLE: 'Motorcycle',
    OFF_ROAD_MOTORCYCLE: 'Off-Road Motorcycle',
    SCOOTER: 'Scooter',
    E_BIKE: 'E-Bike',
    GOLFCART: 'Golf Cart',
    GOKART: 'Go-Kart',
    LSV: 'LSV',
    SNOWMOBILE: 'Snowmobile',
    PWC: 'Personal Watercraft',
    BOAT: 'Boat',
    UTV: 'UTV',
    MOPED: 'Moped',
    TRIKE: 'Trike',
    TRAILER: 'Trailer',
    CAR: 'Car',
    TRUCK: 'Truck',
    OTHER: 'Other',
};
const VEHICLE_CATEGORY_VALUES = new Set(Object.values(VehicleCategory));
function isVehicleCategory(value) {
    return VEHICLE_CATEGORY_VALUES.has(value);
}
const InventoryVehicleTypeMapping = {
    [VehicleCategory.BICYCLE]: VehicleType.NON_TITLED_VEHICLE,
    [VehicleCategory.SXS]: VehicleType.ORV,
    [VehicleCategory.UTV]: VehicleType.ORV,
    [VehicleCategory.ATV]: VehicleType.ATV,
    [VehicleCategory.DUAL_SPORT]: VehicleType.DUAL_SPORT,
    [VehicleCategory.MOTORCYCLE]: VehicleType.MOTORCYCLE,
    [VehicleCategory.OFF_ROAD_MOTORCYCLE]: VehicleType.ORV,
    [VehicleCategory.SCOOTER]: VehicleType.MOTORSCOOTER,
    [VehicleCategory.MOPED]: VehicleType.MOPED,
    [VehicleCategory.E_BIKE]: VehicleType.NON_TITLED_VEHICLE,
    [VehicleCategory.GOLFCART]: VehicleType.NON_TITLED_VEHICLE,
    [VehicleCategory.GOKART]: VehicleType.NON_TITLED_VEHICLE,
    [VehicleCategory.LSV]: VehicleType.LOW_SPEED,
    [VehicleCategory.SNOWMOBILE]: VehicleType.OHV,
    [VehicleCategory.PWC]: VehicleType.VESSEL,
    [VehicleCategory.BOAT]: VehicleType.VESSEL,
    [VehicleCategory.TRIKE]: VehicleType.MOTORCYCLE,
    [VehicleCategory.TRAILER]: VehicleType.TRAILER,
    [VehicleCategory.CAR]: VehicleType.PASSENGER,
    [VehicleCategory.TRUCK]: VehicleType.TRUCK,
    [VehicleCategory.OTHER]: VehicleType.COMMERCIAL,
};
/** Categories the auto build path builds straight into CatalogVehicles: cars and pickups. */
const AUTO_VEHICLE_CATEGORIES = [
    VehicleCategory.CAR,
    VehicleCategory.TRUCK,
];
/**
 * Vehicle categories not supported for online sales on the Ekho platform.
 * Used to gate both inventory matching and canon vehicle building.
 */
const UNSUPPORTED_ONLINE_SALE_CATEGORIES = [
    VehicleCategory.BOAT,
    VehicleCategory.PWC,
    VehicleCategory.SNOWMOBILE,
    VehicleCategory.TRAILER,
    VehicleCategory.CAR,
    VehicleCategory.TRUCK,
    VehicleCategory.OTHER,
];
const BODY_TYPE_TO_VEHICLE_CATEGORY = {
    // Cars
    Cabriolet: VehicleCategory.CAR,
    Convertible: VehicleCategory.CAR,
    Coupe2D: VehicleCategory.CAR,
    Hatchback3D: VehicleCategory.CAR,
    Limousine: VehicleCategory.CAR,
    Sedan4D: VehicleCategory.CAR,
    StationWagon5D: VehicleCategory.CAR,
    TwoDoor: VehicleCategory.CAR,
    FourDoor: VehicleCategory.CAR,
    PassengerVan: VehicleCategory.CAR,
    // Trucks
    Pickup: VehicleCategory.TRUCK,
    Truck: VehicleCategory.TRUCK,
    TruckTractor: VehicleCategory.TRUCK,
    TruckVan: VehicleCategory.TRUCK,
    // Other
    Ambulance: VehicleCategory.OTHER,
    Bus: VehicleCategory.OTHER,
    ManufacturedHome: VehicleCategory.OTHER,
    MotorHome: VehicleCategory.OTHER,
    SpecialMobileMachinery: VehicleCategory.OTHER,
    Tractor: VehicleCategory.OTHER,
    // Motorcycles
    Motorcycle: VehicleCategory.MOTORCYCLE,
    Roadster: VehicleCategory.MOTORCYCLE,
    OffRoadMotorcycle: VehicleCategory.OFF_ROAD_MOTORCYCLE,
    DualSport: VehicleCategory.DUAL_SPORT,
    Trike: VehicleCategory.TRIKE,
    // Powersports
    ATV: VehicleCategory.ATV,
    SideBySide: VehicleCategory.SXS,
    UTV: VehicleCategory.UTV,
    UtilityPassengerSUV: VehicleCategory.CAR,
    UtilityTruck: VehicleCategory.UTV,
    Scooter: VehicleCategory.SCOOTER,
    Moped: VehicleCategory.MOPED,
    Snowmobile: VehicleCategory.SNOWMOBILE,
    // Light electric / micro-mobility
    'E-Bike': VehicleCategory.E_BIKE,
    Gokart: VehicleCategory.GOKART,
    GolfCart: VehicleCategory.GOLFCART,
    // Boats / watercraft
    PontoonBoat: VehicleCategory.BOAT,
    FishingBoat: VehicleCategory.BOAT,
    Bowrider: VehicleCategory.BOAT,
    CenterConsole: VehicleCategory.BOAT,
    DeckBoat: VehicleCategory.BOAT,
    CabinCruiser: VehicleCategory.BOAT,
    Sailboat: VehicleCategory.BOAT,
    BoatOther: VehicleCategory.BOAT,
    Jetski: VehicleCategory.PWC,
    PersonalWatercraft: VehicleCategory.PWC,
    // Trailers
    Trailer: VehicleCategory.TRAILER,
    MultipurposeTrailer: VehicleCategory.TRAILER,
    TrailerUtility: VehicleCategory.TRAILER,
    CamperTrailer: VehicleCategory.TRAILER,
    TrailerCoach: VehicleCategory.TRAILER,
    Bicycle: VehicleCategory.BICYCLE,
};
/**
 * Maps (vehicleType, bodyType) pairs to VehicleCategory when the body-type-only
 * default is insufficient. Keyed by VehicleType, then BodyType value.
 */
const VEHICLE_TYPE_BODY_TYPE_OVERRIDES = {
    // A low-speed vehicle is an LSV whatever it is bodied as. The car-like bodies are
    // listed because their base mapping is `Car` and `Truck`, which would classify a
    // 25 mph FMVSS 500 machine as a road vehicle: a GEM e6 bodied FourDoor and a
    // Vantage V9 bodied Truck are both LSVs, and the category decides how they title.
    [VehicleType.LOW_SPEED]: {
        GolfCart: VehicleCategory.LSV,
        SideBySide: VehicleCategory.LSV,
        FourDoor: VehicleCategory.LSV,
        TwoDoor: VehicleCategory.LSV,
        PassengerVan: VehicleCategory.LSV,
        Truck: VehicleCategory.LSV,
    },
    [VehicleType.OHV]: {
        Gokart: VehicleCategory.GOKART,
        GolfCart: VehicleCategory.GOLFCART,
        OffRoadMotorcycle: VehicleCategory.OFF_ROAD_MOTORCYCLE,
        Motorcycle: VehicleCategory.OFF_ROAD_MOTORCYCLE,
        SideBySide: VehicleCategory.SXS,
        DualSport: VehicleCategory.DUAL_SPORT,
    },
    [VehicleType.NON_TITLED_VEHICLE]: {
        Gokart: VehicleCategory.GOKART,
        GolfCart: VehicleCategory.GOLFCART,
        Scooter: VehicleCategory.SCOOTER,
        Moped: VehicleCategory.MOPED,
    },
    [VehicleType.ORV]: {
        SideBySide: VehicleCategory.SXS,
        UTV: VehicleCategory.UTV,
        UtilityTruck: VehicleCategory.UTV,
        OffRoadMotorcycle: VehicleCategory.OFF_ROAD_MOTORCYCLE,
    },
    [VehicleType.DUAL_SPORT]: {
        DualSport: VehicleCategory.DUAL_SPORT,
        Motorcycle: VehicleCategory.DUAL_SPORT,
        OffRoadMotorcycle: VehicleCategory.DUAL_SPORT,
    },
    [VehicleType.COMMERCIAL]: {
        Ambulance: VehicleCategory.OTHER,
        Bus: VehicleCategory.OTHER,
        ManufacturedHome: VehicleCategory.OTHER,
        MotorHome: VehicleCategory.OTHER,
        SpecialMobileMachinery: VehicleCategory.OTHER,
        Tractor: VehicleCategory.OTHER,
        TruckVan: VehicleCategory.OTHER,
        TruckTractor: VehicleCategory.OTHER,
        PassengerVan: VehicleCategory.OTHER,
    },
};
function deriveVehicleCategoryFromBodyType(bodyType, vehicleType) {
    if (!(bodyType in BODY_TYPE_TO_VEHICLE_CATEGORY)) {
        return null;
    }
    if (vehicleType) {
        const overrides = VEHICLE_TYPE_BODY_TYPE_OVERRIDES[vehicleType];
        const override = overrides?.[bodyType];
        if (override) {
            return override;
        }
    }
    return BODY_TYPE_TO_VEHICLE_CATEGORY[bodyType];
}
const UseType = {
    PERSONAL: 'Personal',
    COMMERCIAL: 'Commercial',
};

export { AUTO_VEHICLE_CATEGORIES, BodyTypeDescriptions, InventoryVehicleTypeMapping, UNSUPPORTED_ONLINE_SALE_CATEGORIES, UseType, VehicleCategory, VehicleType, VehicleTypeDescriptions, deriveVehicleCategoryFromBodyType, isVehicleCategory };
//# sourceMappingURL=VehicleTypes.js.map
